import { supabase } from "@/integrations/supabase/client";

export type ProfilePreference = "first_name" | "mirror_tone" | "show_mirror_suggestions" | "notifications_daily_brief" | "notifications_alerts";

/** A successful HTTP response is insufficient: RLS can match no rows. */
export function requireSavedPreference(rows: unknown, userId: string, values: Record<string, unknown>) {
  if (!Array.isArray(rows) || rows.length !== 1 || rows[0]?.user_id !== userId ||
      Object.entries(values).some(([key, value]) => rows[0][key] !== value)) {
    throw new Error("Preference save was not confirmed");
  }
}

export async function preferenceSession(userId: string) {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session || session.user.id !== userId) throw new Error("Settings account changed");
  return session;
}

export async function saveProfilePreference(userId: string, key: ProfilePreference, value: string | boolean) {
  const session = await preferenceSession(userId);
  await withPreferenceTimeout(async signal => {
    const { data, error } = await supabase.from("user_profiles")
      .update({ [key]: value }).eq("user_id", userId).select(`user_id, ${key}`)
      .setHeader("Authorization", `Bearer ${session.access_token}`).abortSignal(signal);
    if (error) throw error;
    requireSavedPreference(data, userId, { [key]: value });
  });
  await preferenceSession(userId);
}

export async function withPreferenceTimeout<T>(request: (signal: AbortSignal) => Promise<T>) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 12000);
  try { return await request(controller.signal); }
  finally { clearTimeout(timer); }
}
